import { CollectionItem, Item } from "@/services/fobizzToolsApi";
import { CollectionFilterParams } from "@/types/collection.types";
import debounce from "lodash.debounce";
import { computed, ref } from "vue";

export const useCollectionFilter = (
  defaultParams: CollectionFilterParams,
  collectionItems: ComputedRef<0 | CollectionItem[] | undefined>
) => {
  const params = ref<CollectionFilterParams>(defaultParams);

  const hasActiveFilters = computed(() => {
    return Object.entries(params.value).some(([key, value]) => {
      if (key === "query") return value !== "";
      if (Array.isArray(value)) return value.length > 0;
      return false;
    });
  });

  const updateFilter = <K extends keyof CollectionFilterParams>(
    key: K,
    value: CollectionFilterParams[K]
  ) => {
    params.value = { ...params.value, [key]: value };
  };

  const resetFilters = () => {
    params.value = { ...defaultParams };
  };

  const search = (query: string) => {
    params.value = { ...params.value, query };
  };

  const debouncedSearch = debounce(search, 300);

  const filteredItems = computed(() => {
    if (!collectionItems.value || !collectionItems.value.length) return [];
    return collectionItems.value.filter((collectionItem) => {
      const item: Item = collectionItem.collectionable;

      if (params.value.query) {
        const matchesTitle = (item.name || "")
          .toLowerCase()
          .includes(params.value.query.toLowerCase());
        const matchesAuthor = item.user.publicDisplayName
          ?.toLowerCase()
          .includes(params.value.query.toLowerCase());

        if (!matchesTitle && !matchesAuthor) {
          return false;
        }
      }

      if (
        params.value.schoolTypes.length > 0 &&
        !(item.schoolTypes || []).some((type) =>
          params.value.schoolTypes.includes(type.toString())
        )
      ) {
        return false;
      }

      if (
        params.value.gradeLevels.length > 0 &&
        !(item.gradeLevels || []).some((grade) =>
          params.value.gradeLevels.includes(grade.toString())
        )
      ) {
        return false;
      }

      if (
        params.value.subjectTypes.length > 0 &&
        !(item.subjectTypes || []).some((type) => {
          return params.value.subjectTypes.includes(type.toString());
        })
      ) {
        return false;
      }

      // Filter by models (AppEnum)
      if (params.value.models.length > 0) {
        return params.value.models.some((model: string) => {
          // Check for ClassroomScreen mapping
          if (
            model === "TeachingBoard::Screen" &&
            item.kind === "ClassroomScreen::Screen"
          ) {
            return true;
          }
          // For all other cases, do a direct comparison
          return item.kind === model;
        });
      }

      return true;
    });
  });

  return {
    params,
    hasActiveFilters,
    updateFilter,
    resetFilters,
    search,
    debouncedSearch,
    filteredItems,
  };
};
